import { domReady, throttle } from '../modules/utils.js';

class CourseContentNav extends HTMLElement {
  static get observedAttributes() {
    return [];
  }

  attributeChangedCallback(name, oldValue, newValue) { }

  async connectedCallback() {
    await domReady();
    this.refs = {
      nav: this.querySelector('.coursecontent-nav'),
      links: [...this.querySelectorAll('.coursecontent-nav-link')],
    };
    if (!this.refs.nav) return;

    this.targets = this.refs.links
      .map((link) => {
        const id = link.getAttribute('href')?.replace(/^#/, '');
        const target = id ? document.getElementById(id) : null;
        return target ? { link, target } : null;
      })
      .filter(Boolean);

    this.#initStuckObserver();
    this.#initScrollSpy();
  }

  disconnectedCallback() {
    this.stuckObserver?.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
  }

  // the line a target's top edge must cross to count as "at the top of the
  // viewport" — just below the sticky topbar + this sticky nav.
  #getActivationLine() {
    const topOffset = parseFloat(getComputedStyle(this.refs.nav).top) || 0;
    const navHeight = this.refs.nav.getBoundingClientRect().height;
    return topOffset + navHeight + 0;
  }

  #initStuckObserver() {
    // a zero-height sentinel placed right before the sticky nav; once it
    // scrolls past the same offset the nav sticks at, the nav has become
    // visually "stuck" rather than just sitting in normal flow.
    const topOffset = parseFloat(getComputedStyle(this.refs.nav).top) || 0;
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'height: 0; margin: 0; padding: 0;';
    this.insertBefore(sentinel, this.refs.nav);

    this.stuckObserver = new IntersectionObserver(
      ([entry]) => {
        this.refs.nav.toggleAttribute('data-stuck', !entry.isIntersecting);
      },
      { rootMargin: `-${topOffset + 1}px 0px 0px 0px`, threshold: 0 }
    );
    this.stuckObserver.observe(sentinel);
  }

  #initScrollSpy() {
    if (!this.targets.length) return;

    this.#onScroll();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  // picks whichever target has already reached the activation line (top
  // edge at or above it) and is nearest to it. Targets still below the
  // line don't count, so nothing is selected until the first one arrives.
  #onScroll = throttle(() => {
    const activationLine = this.#getActivationLine();
    let nearest = null;
    let nearestDistance = Infinity;

    for (const item of this.targets) {
      const top = item.target.getBoundingClientRect().top;
      if (top > activationLine) continue;

      const distance = activationLine - top;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = item;
      }
    }

    this.#setActiveLink(nearest ? nearest.link : null);
  }, 100);

  #setActiveLink(activeLink) {
    for (const { link } of this.targets) {
      link.classList.toggle('is-active', link === activeLink);
    }
  }
}

customElements.define('course-content-nav', CourseContentNav);
